import { useRef, useState, useCallback, useEffect } from "react";
import { Send, Plus, X, ImageIcon } from "lucide-react";
import { Button } from "@/components/flowly/infra/Button/Button";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAnonymousGate } from "@/hooks/useAnonymousGate";
import { useImageUpload } from "@/hooks/useImageUpload";
import { toast } from "@/lib/toast";
import type { ChatReferenceImage } from "./types";

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

interface ChatInputBarProps {
  inputValue: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onImagesChange: (images: ChatReferenceImage[]) => void;
  referenceImages: ChatReferenceImage[];
  maxImages?: number;
  disabled?: boolean;
  placeholder?: string;
}

export const ChatInputBar = ({
  inputValue,
  onInputChange,
  onSend,
  onImagesChange,
  referenceImages,
  maxImages = 5,
  disabled = false,
  placeholder,
}: ChatInputBarProps) => {
  const { t } = useLanguage();
  const { normalizeFilesOrWarn } = useImageUpload();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Built-in auth gate: every "send" path (button click, Enter key) funnels
  // through gatedSend, so an anon user sees the canonical signup modal the
  // moment they try to message the assistant — mirrors the GenerateButton
  // pattern so any future chat-style flow inherits this automatically.
  const gatedSend = useAnonymousGate(onSend);

  useEffect(() => {
    if (!disabled) {
      inputRef.current?.focus();
    }
  }, [disabled]);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      gatedSend();
    }
  };

  const processFiles = useCallback(
    async (rawFiles: FileList | File[]) => {
      const remainingSlots = maxImages - referenceImages.length;
      const rawList = Array.from(rawFiles).slice(0, remainingSlots);

      const filesToProcess = await normalizeFilesOrWarn(rawList);
      if (!filesToProcess) return;

      filesToProcess.forEach((file) => {
        if (!file.type.startsWith("image/")) return;

        if (file.size > MAX_FILE_SIZE_BYTES) {
          toast.error(t.chatInput.fileTooLarge, {
            description: t.chatInput.fileTooLargeDesc,
          });
          return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
          const base64 = event.target?.result as string;
          const newImage: ChatReferenceImage = {
            id: `ref-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            url: base64,
            base64: base64,
          };
          onImagesChange([...referenceImages, newImage]);
        };
        reader.readAsDataURL(file);
      });
    },
    [
      referenceImages,
      maxImages,
      onImagesChange,
      t.chatInput.fileTooLarge,
      t.chatInput.fileTooLargeDesc,
      normalizeFilesOrWarn,
    ],
  );

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    processFiles(files);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleRemoveImage = (id: string) => {
    onImagesChange(referenceImages.filter((img) => img.id !== id));
  };

  // Drag & Drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (referenceImages.length < maxImages) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (referenceImages.length >= maxImages) return;

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      processFiles(files);
    }
  };

  const canAddMore = referenceImages.length < maxImages;

  return (
    <div className="py-4 border-t border-border">
      {/* Reference Images Thumbnails (inside input area) */}
      {referenceImages.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3">
          {referenceImages.map((image) => (
            <div
              key={image.id}
              className="relative group w-12 h-12 rounded-lg overflow-hidden border border-border"
            >
              <img
                src={image.url}
                alt="Reference"
                className="w-full h-full object-cover"
              />
              <button
                onClick={() => handleRemoveImage(image.id)}
                className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          ))}
          {referenceImages.length > 0 && (
            <span className="text-xs text-muted-foreground self-center ms-1">
              {referenceImages.length}/{maxImages}
            </span>
          )}
        </div>
      )}

      {/* Input Row */}
      <div
        className={cn(
          "flex gap-3 relative rounded-2xl transition-colors",
          isDragging &&
            "ring-2 ring-primary ring-offset-2 ring-offset-background",
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Upload Button (inside input area) */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileUpload}
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={!canAddMore || disabled}
          className={cn(
            "h-12 px-3 rounded-2xl border border-border bg-secondary flex items-center gap-2 transition-colors flex-shrink-0",
            !canAddMore || disabled
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-muted hover:border-primary/50",
          )}
          title={t.chatInput.addReference}
        >
          <Plus className="w-4 h-4 text-muted-foreground" />
          <ImageIcon className="w-4 h-4 text-muted-foreground" />
        </button>

        <textarea
          ref={inputRef}
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder={placeholder || t.chatInput.placeholder}
          className="flex-1 resize-none rounded-2xl border border-border bg-secondary px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 min-h-[48px] max-h-[120px]"
          rows={1}
          disabled={disabled}
        />

        <Button
          onClick={gatedSend}
          disabled={!inputValue.trim() || disabled}
          className="px-4 flex-shrink-0"
        >
          <Send className="w-5 h-5" />
        </Button>
      </div>

      {/* Drag hint */}
      {isDragging && (
        <div className="absolute inset-0 bg-primary/10 border-2 border-dashed border-primary rounded-2xl flex items-center justify-center pointer-events-none">
          <p className="text-primary font-medium">{t.chatInput.dropToAttach}</p>
        </div>
      )}
    </div>
  );
};
